import http from "../utils/http"

export interface PriceHistoryEntry {
  id: number;
  price: string;
  observedAt: string;
}

export interface HomeSummary {
  id: number;
  fullAddress: string;
  status: string;
  lat: string;
  lng: string;
  imageUrl: string | null;
  upgradesRoi: number | null;
  upgradesTotalCost: number | null;
  upgradesHeatPumpGrant: number | null;
  upgradesTotalAnnualSavings: number | null;
  upgradesPaybackPeriod: number | null;
  upgradesCurrentEnergyCost: number | null;
  features: { numberOfHabitableRooms?: number; propertyType?: string; floorArea?: number; [key: string]: unknown } | null;
}

export interface ScrapedPropertySummary {
  id: number;
  rightmoveId: string;
  rightmoveUrl: string;
  title: string;
  address: string;
  postcode: string;
  price: string | null;
  bedrooms: number | null;
  propertyType: string;
  epcRating: string;
  floorAreaSqm: string | null;
  daysOnMarket: number | null;
  home: HomeSummary;
}

export interface Opportunity {
  id: number;
  portfolio: number;
  listingStatus: string;
  kanbanStage: string | null;
  currentPrice: string | null;
  lastCheckedAt: string | null;
  createdAt: string;
  scrapedProperty: ScrapedPropertySummary;
}

export interface OpportunityDetail extends Opportunity {
  scrapedProperty: ScrapedPropertySummary & {
    priceHistory: PriceHistoryEntry[];
    rentEstimate: number | null;
    rentEstimateFetchedAt: string | null;
    grossYield: number | null;
  };
}

export interface KanbanCard extends Opportunity {
  kanbanStageUpdatedAt: string | null;
  isStale: boolean;
}

export interface PaginatedOpportunities {
  count: number;
  next: string | null;
  previous: string | null;
  results: Opportunity[];
}

export interface OpportunityFilters {
  search?: string
  builtForm?: string
  ordering?: "recent" | "oldest" | "price_asc" | "price_desc" | "bedrooms" | "highest_roi"
}

export const listOpportunities = async (
  portfolioId: number,
  page: number = 1,
  filters: OpportunityFilters = {}
): Promise<PaginatedOpportunities> => {
  const params = new URLSearchParams({ portfolio_id: String(portfolioId), page: String(page) })
  if (filters.search) params.set("search", filters.search)
  if (filters.builtForm) params.set("built_form", filters.builtForm)
  if (filters.ordering) params.set("ordering", filters.ordering)
  const response = await http.get(`/api/sourcing/opportunities/?${params}`)
  return response.data
}

export const getOpportunityDetail = async (id: number): Promise<OpportunityDetail> => {
  const response = await http.get(`/api/sourcing/opportunities/${id}/`)
  return response.data
}

export const markAsInterested = async (id: number): Promise<Opportunity> => {
  const response = await http.patch(`/api/sourcing/opportunities/${id}/`, {
    kanban_stage: "interested",
  })
  return response.data
}

export const fetchKanbanCards = async (portfolioId: number): Promise<KanbanCard[]> => {
  const response = await http.get(`/api/sourcing/opportunities/kanban/?portfolio_id=${portfolioId}`)
  return response.data
}

export const updateKanbanStage = async (
  id: number,
  kanbanStage: string
): Promise<OpportunityDetail> => {
  const response = await http.patch(`/api/sourcing/opportunities/${id}/`, {
    kanban_stage: kanbanStage,
  })
  return response.data
}
